import React, { MouseEvent, useState } from 'react';
import cn from 'clsx';

import CheckboxGroup from './components/checkbox-group/checkbox-group';
import { CheckSolidIcon } from '../../shared/icons/icons';
import Typography from '../typography/typography';

import styles from './checkbox.scss';

interface CheckboxProps {
  ariaLabel?: string;
  ariaLabelledBy?: string;
  className?: string;
  id?: string;
  isChecked?: boolean;
  isDisabled?: boolean;
  label?: React.ReactNode;
  name?: string;
  onClick?: (value?: string, event?: MouseEvent) => void;
  value?: string;
}

function Checkbox(props: React.PropsWithChildren<CheckboxProps>) {
  const {
    ariaLabel,
    ariaLabelledBy,
    children,
    className = '',
    id,
    isChecked: isCheckedProp,
    isDisabled,
    label,
    name,
    onClick,
    value
  } = props;
  const [isChecked, setIsChecked] = useState(isCheckedProp || false);
  const isCheckedVal =
    typeof isCheckedProp === 'boolean' ? isCheckedProp : isChecked;

  function handleClick(e) {
    e.preventDefault();
    if (!isDisabled) {
      if (onClick) {
        onClick(value, e);
      }

      if (typeof isCheckedProp !== 'boolean') {
        setIsChecked(!isChecked);
      }
    }
  }

  return (
    <Typography
      className={cn(
        styles['checkbox'],
        `checkbox ${className} ${isCheckedVal ? 'checkbox--checked' : ''}`
      )}
      onClick={handleClick}
      onKeyPress={handleClick}
      role="none"
      tabIndex={label ? 0 : -1}
      tag="div"
      variant="P"
    >
      <span className={cn(styles['container'], 'checkbox__container')}>
        <input
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          checked={isCheckedVal}
          disabled={isDisabled}
          id={id}
          name={name}
          readOnly
          tabIndex={!label ? 0 : -1}
          type="checkbox"
          value={value}
        />

        <span
          className={cn(
            `${styles['box']} ${
              isCheckedVal ? styles['display-checked'] : styles['display']
            } ${isDisabled ? styles['checkbox-disabled'] : ''}`,
            'checkbox__icon'
          )}
        >
          <CheckSolidIcon className={styles['icon']} />
        </span>
      </span>
      {label && (
        <label
          className={`${styles['label']} ${
            isDisabled ? styles['label-disabled'] : ''
          }`}
          data-testid="checkbox_label"
          htmlFor={id}
        >
          {label}
        </label>
      )}
      {children}
    </Typography>
  );
}

export { CheckboxGroup };
export default Checkbox;
